import React, { useState, useRef, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';

import CardDesignerCard from './components/CardDesignerCard';
import DesignEditor from '../DesignEditor';
import DesignEditorHeader, {
  DesignEditorHeaderCenterSlot,
  DesignEditorHeaderLeftSlot,
  DesignEditorHeaderRightSlot,
} from '../DesignEditor/components/DesignEditorHeader';
import Button from '../Button';
import useCardDesigner from './useCardDesigner';
import {
  CARD_ELEMENT_TYPE,
  CARD_SIDE,
  ORIENTATION,
} from '../../../services/cards/constants';
import { getCardSideElementsConfiguration } from './helpers';
import CardDesignerControlPanel from './components/CardDesignerControlPanel';
import DesignEditorPreviewPane, {
  DesignEditorPreviewContent,
} from '../DesignEditor/components/DesignEditorPreviewPane';
import CardDesignerCardElementControlToolbar from './components/CardDesignerCardElements/CardDesignerCardElementControlToolbar';
import { isElementCardBackground } from './components/CardDesignerControlPanel/components/CardDesignerBackgroundControls/helpers';
import { isElementCardForeground } from './components/CardDesignerControlPanel/components/CardDesignerForegroundControls/helpers';
import { isElementCardLogo } from './components/CardDesignerControlPanel/components/CardDesignerLogoControls/helpers';
import Angle from '../Angle';
import Input from '../Input';
import useOpenAlert from '../../../hooks/useOpenAlert';
import useRequestConfirm from '../../../hooks/useRequestConfirm';
import FullScreenLoader from '../FullScreenLoader';
import { DESIGN_MODES } from './constants';
import { isCardElementEditable } from '../../../services/cards/components/AccountCard/helpers';

const propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  onComplete: PropTypes.func.isRequired,
  defaultCardId: PropTypes.string,
  defaultCardSide: PropTypes.oneOf(Object.values(CARD_SIDE)),
  defaultCardSideId: PropTypes.string,
  title: PropTypes.string,
  customPrintName: PropTypes.string,
  designMode: PropTypes.oneOf(Object.values(DESIGN_MODES)),
  printPartnerId: PropTypes.string,
};

const CardDesigner = ({
  className,
  onComplete,
  onClose,
  defaultCardId,
  defaultCardSide = CARD_SIDE.FRONT,
  defaultCardSideId,
  title = 'Create your Custom Print',
  customPrintName: currentCustomPrintName = '',
  designMode,
  printPartnerId,
}) => {
  const classes = classNames({
    [className]: !!className,
    'custom-print-card-designer': true,
    'collages-card-designer': designMode === DESIGN_MODES.COLLAGES,
  });
  const [customPrintName, setCustomPrintName] = useState(
    currentCustomPrintName,
  );
  const [gridEnabled, setGridEnabled] = useState(false);
  const [lastUsedFont, setLastUsedFont] = useState();
  const openAlert = useOpenAlert();
  const requestConfirm = useRequestConfirm();
  const hasShownMemoryMateAlert = useRef(false);

  const cardRef = useRef();
  const { cardId = defaultCardId } = useParams();
  const {
    card,
    cardSide,
    refetchCard,
    onCardElementCreate,
    onCardSettingsUpdate,
    onCardElementDelete,
    selectedCardElementId,
    onCardElementSelect,
    onCardElementUpdate,
    onCardElementsUpdate,
    onElementConfigurationChange,
  } = useCardDesigner({
    defaultCardId: cardId,
    defaultCardSideId,
    // Custom Prints should now default to Portrait mode.
    defaultCardOrientation:
      !designMode || designMode === DESIGN_MODES.ID_CARDS
        ? ORIENTATION.PORTRAIT
        : undefined,
    side: defaultCardSide,
    designMode,
    printPartnerId,
  });

  useEffect(() => {
    if (hasShownMemoryMateAlert.current) return;
    if (!card || !cardSide) return;

    const templateName = (
      cardSide.card_template?.print_size?.name ?? ''
    ).toLowerCase();

    if (!templateName.includes('memory mate')) {
      return;
    }

    const sideElements = getCardSideElementsConfiguration(cardSide) ?? [];
    const editablePhotos = sideElements.filter(
      element =>
        element.cardElement.type === CARD_ELEMENT_TYPE.PHOTO &&
        element.side === CARD_SIDE.FRONT &&
        isCardElementEditable(element),
    );

    if (editablePhotos.length !== 2) {
      return;
    }

    const orientations = editablePhotos.map(el => {
      const { width, height } = el.cardElement.frame ?? {};
      if (!width || !height) return null;
      return width > height ? ORIENTATION.LANDSCAPE : ORIENTATION.PORTRAIT;
    });

    if (
      !orientations.includes(ORIENTATION.PORTRAIT) ||
      !orientations.includes(ORIENTATION.LANDSCAPE)
    ) {
      return;
    }

    if (
      editablePhotos?.find(
        ({ cardElementConfiguration }) =>
          cardElementConfiguration?.help_placeholder === 'TEAM',
      )
    ) {
      return;
    }

    const cutoffDate = new Date('2026-05-18T00:00:00Z');
    const updatedAt = card.updated_at ? new Date(card.updated_at) : null;

    if (!!updatedAt && updatedAt >= cutoffDate) {
      return;
    }

    hasShownMemoryMateAlert.current = true;
    openAlert({
      type: 'warning',
      message: (
        <div className="text-center">
          <b>Warning:</b> <br />
          It appears your landscape photo slot within this product is not
          utilizing the new <b className="text-primary">TEAM</b> photo slot. If
          this slot is intended for group photo, please replace it with the new{' '}
          <b className="text-primary">TEAM</b> photo slot. This will ensure the
          group photos fit as expected.
        </div>
      ),
    });
  }, [card?.id, cardSide?.id, openAlert]);

  if (!cardSide) {
    return <FullScreenLoader />;
  }

  const elements = getCardSideElementsConfiguration(cardSide);
  const selectedCardElement = elements.find(
    element => element.cardElement.id === selectedCardElementId,
  );
  const backgroundElement = elements.find(element =>
    isElementCardBackground(element, designMode !== DESIGN_MODES.COLLAGES),
  );
  const foregroundElement = elements.find(element =>
    isElementCardForeground(element),
  );

  const logoElement = elements.find(element => isElementCardLogo(element));

  const editablePhotoElements = (elements ?? []).filter(
    element =>
      element.cardElement.type === CARD_ELEMENT_TYPE.PHOTO &&
      element.side === CARD_SIDE.FRONT &&
      isCardElementEditable(element),
  );

  const handleComplete = async () => {
    await onComplete({
      card,
      name: customPrintName,
    });
  };

  return (
    <DesignEditor className={classes}>
      <DesignEditorHeader>
        <DesignEditorHeaderLeftSlot>
          {onClose && (
            <Button
              btnStyle="link"
              className="text-bold"
              onClick={() =>
                requestConfirm({
                  message: (
                    <Fragment>
                      <h4 className="text-center">
                        Exit custom print configurator?
                      </h4>
                      <p className="text-center text-light">
                        Your progress will be lost.
                      </p>
                    </Fragment>
                  ),

                  confirmText: 'Exit now',
                  confirmBtnStyle: 'danger',
                  confirmBtnOutline: true,
                  cancelText: "Don't exit",
                  cancelBtnStyle: 'primary',
                  onConfirm: () => onClose(),
                  onCancel: () => {},
                })
              }
            >
              <Angle direction="left" />
              Back
            </Button>
          )}
          {designMode === DESIGN_MODES.COLLAGES && (
            <Input
              id="custom-print-name"
              placeholder="Name your collage template"
              value={customPrintName}
              className="ml-2"
              onChange={({ target }) => setCustomPrintName(target.value)}
            />
          )}
        </DesignEditorHeaderLeftSlot>

        <DesignEditorHeaderCenterSlot>
          <h3 className="design-editor-header-title">
            {designMode !== DESIGN_MODES.COLLAGES && customPrintName
              ? `Editing ${customPrintName}`
              : title}
          </h3>
        </DesignEditorHeaderCenterSlot>

        <DesignEditorHeaderRightSlot>
          <Button
            btnSize="sm"
            onClick={() => {
              if (designMode === DESIGN_MODES.COLLAGES && !customPrintName) {
                openAlert({
                  type: 'error',
                  message: 'Please enter a name for your collage',
                });
                return;
              }
              if (editablePhotoElements.length === 0) {
                requestConfirm({
                  message: (
                    <Fragment>
                      <h4 className="text-center">Heads up!</h4>
                      <p>
                        It looks like your Custom Print does not have any
                        editable photo elements. If you save your Custom Print
                        without any editable photo elements, your customers will
                        not be able to add their photos to the final product.
                      </p>
                      <p className="text-center">
                        Are you sure you want to continue?
                      </p>
                    </Fragment>
                  ),

                  confirmText: 'Continue',
                  onConfirm: async () => {
                    handleComplete();
                    onClose?.();
                  },
                  cancelText: 'Go back',
                  onCancel: () => {},
                });
              } else {
                handleComplete();
                onClose?.();
              }
            }}
          >
            Save
          </Button>
        </DesignEditorHeaderRightSlot>
      </DesignEditorHeader>

      <CardDesignerControlPanel
        onCardSettingsUpdate={onCardSettingsUpdate}
        onCardElementCreate={onCardElementCreate}
        onCardElementSelect={onCardElementSelect}
        onCardElementUpdate={onCardElementUpdate}
        onCardElementsUpdate={onCardElementsUpdate}
        onCardElementDelete={onCardElementDelete}
        card={card}
        cardSide={cardSide}
        cardRef={cardRef}
        refetchCard={refetchCard}
        onElementConfigurationChange={onElementConfigurationChange}
        elements={elements}
        selectedCardElement={selectedCardElement}
        backgroundElement={backgroundElement}
        foregroundElement={foregroundElement}
        logoElement={logoElement}
        printPartnerId={printPartnerId}
        gridEnabled={gridEnabled}
        setGridEnabled={setGridEnabled}
        lastUsedFont={lastUsedFont}
        designMode={designMode}
      />

      <DesignEditorPreviewPane>
        <CardDesignerCardElementControlToolbar
          onCardElementCreate={onCardElementCreate}
          onCardElementUpdate={onCardElementUpdate}
          onCardElementDelete={onCardElementDelete}
          onElementConfigurationChange={onElementConfigurationChange}
          element={selectedCardElement}
          cardSide={cardSide}
          designMode={designMode}
          setLastUsedFont={setLastUsedFont}
        />

        <DesignEditorPreviewContent
          onClick={({ target }) => {
            if (target?.className === 'design-editor-preview-content') {
              onCardElementSelect(undefined);
            }
          }}
        >
          {card && cardSide && (
            <CardDesignerCard
              card={card}
              cardRef={cardRef}
              cardSide={cardSide}
              selectedCardElementId={selectedCardElementId}
              onCardElementSelect={onCardElementSelect}
              onCardElementUpdate={onCardElementUpdate}
              onCardElementDelete={onCardElementDelete}
              onElementConfigurationChange={onElementConfigurationChange}
              gridEnabled={gridEnabled}
              designMode={designMode}
            />
          )}
        </DesignEditorPreviewContent>
      </DesignEditorPreviewPane>
    </DesignEditor>
  );
};

CardDesigner.propTypes = propTypes;

export default CardDesigner;
